import type {
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  QueryFilter,
} from "@/types/campaign";
import {
  rangeFilterToForm,
  typeOfAuditorAssistanceOptions,
} from "@/utils/filter-utils";
import {
  formatMoney,
  formatMoneyRange,
  formatPercentage,
  formatPercentageRange,
} from "@/utils/money";
import { isOneOf } from "@/utils/type-guards";
import { Coins } from "lucide-react";
import { match, P } from "ts-pattern";
import { getFilterByFilterName, type FilterCategory } from "../config";
import { createFilterGroup, filterGroupTypes } from "../filter-types";

export function createRangeDisplayValue(
  filter:
    | QueryFilter
    | EventQueryFilter
    | DepartmentSizeQueryFilter
    | undefined,
  type: "number" | "percentage" = "number",
): string {
  if (
    isOneOf(filter?.operator, "EventFilter", "DepartmentSize") ||
    !filter?.values
  ) {
    filter = undefined;
  }
  const { min, max } = rangeFilterToForm(filter);
  return match({ min, max })
    .with({ min: P.number, max: P.number }, ({ min, max }) => {
      if (min === max && filter?.operator === "Equal") {
        return type === "number"
          ? formatMoney(min)
          : formatPercentage(min / 100);
      }
      return type === "number"
        ? formatMoneyRange(min, max)
        : formatPercentageRange(min / 100, max / 100);
    })
    .with(
      { min: P.number },
      ({ min }) =>
        `Over ${type === "number" ? formatMoney(min) : formatPercentage(min / 100)}`,
    )
    .with(
      { max: P.number },
      ({ max }) =>
        `Under ${type === "number" ? formatMoney(max) : formatPercentage(max / 100)}`,
    )
    .otherwise(() => "Ikke angivet");
}

function createDisplayValue(
  filter:
    | QueryFilter
    | EventQueryFilter
    | DepartmentSizeQueryFilter
    | undefined,
  changeFilter:
    | QueryFilter
    | EventQueryFilter
    | DepartmentSizeQueryFilter
    | undefined,
  ignoreOperator = false,
  type: "number" | "percentage" = "number",
): string {
  if (ignoreOperator) {
    if (!changeFilter) {
      return filter?.values?.[0]
        ? type === "number"
          ? formatMoney(parseInt(filter?.values?.[0]))
          : formatPercentage(parseInt(filter?.values?.[0]) / 100)
        : "-";
    }
    return `${filter?.values?.[0] ? (type === "number" ? formatMoney(parseInt(filter.values[0])) : formatPercentage(parseInt(filter.values[0]) / 100)) : "-"} ${changeFilter?.values?.[0] ? ", " + formatPercentage(parseInt(changeFilter.values[0])) + " i vækst" : ""}`;
  }

  const baseDescription = filter?.values
    ? createRangeDisplayValue(filter, type)
    : "";
  const growthPercentage = changeFilter?.values?.[0];

  if (!baseDescription) {
    return growthPercentage ? `${growthPercentage} % i vækst` : "Ikke angivet";
  }

  return growthPercentage
    ? `${baseDescription}, ${growthPercentage} % i vækst`
    : baseDescription;
}

export const economyFilterCategory = {
  id: "economy",
  title: "Økonomi",
  icon: Coins,
  description: "Virksomhedernes regnskabs- og nøgletal",
  filters: {
    grossProfit: createFilterGroup({
      id: "gross-profit",
      title: "Bruttofortjeneste",
      keywords: ["omsætning"],
      description:
        "Virksomhedernes bruttofortjeneste, som viser omsætning minus vareforbrug. Kan anvendes som alternativ for omsætning, da denne ofte ikke bliver oplyst.",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].GrossProfitLoss.Value",
          filterName: "economy-gross-profit-value",
        },
        change: {
          fieldName: "Financial.Reports[0].GrossProfitLoss.Change",
          filterName: "economy-gross-profit-change",
        },
      },
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i bruttofortjeneste over tid, når man sammenligner de 2 seneste regnskaber.",
      filterType: filterGroupTypes.economy,
      type: "number",
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-gross-profit-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-gross-profit-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    ebitda: createFilterGroup({
      id: "ebitda",
      title: "EBITDA",
      keywords: ["driftsresultat"],
      description:
        "Virksomhedernes EBITDA, der viser driftsresultatet før renter, skat og afskrivninger.",
      filterType: filterGroupTypes.economy,
      type: "number",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i EBITDA over tid, når man sammenligner de 2 seneste regnskaber.",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].EBITDA.Value",
          filterName: "economy-ebitda-value",
        },
        change: {
          fieldName: "Financial.Reports[0].EBITDA.Change",
          filterName: "economy-ebitda-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-ebitda-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-ebitda-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    netProfit: createFilterGroup({
      id: "net-profit",
      title: "Årets resultat",
      keywords: ["overskud", "underskud"],
      description:
        "Virksomhedernes samlede overskud eller underskud efter alle poster er medregnet.",
      filterType: filterGroupTypes.economy,
      type: "number",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i årets resultat over tid, når man sammenligner de 2 seneste regnskaber.",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].ProfitLoss.Value",
          filterName: "economy-net-profit-value",
        },
        change: {
          fieldName: "Financial.Reports[0].ProfitLoss.Change",
          filterName: "economy-net-profit-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-net-profit-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-net-profit-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    operatingProfit: createFilterGroup({
      id: "operating-profit",
      title: "Driftsresultat",
      keywords: ["primært resultat", "drift", "EBIT"],
      description:
        "Virksomhedernes driftsresultat, som viser resultatet af den ordinære drift før finansielle poster (EBIT).",
      filterType: filterGroupTypes.economy,
      type: "number",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i driftsresultat over tid, når man sammenligner de 2 seneste regnskaber.",
      filters: {
        value: {
          fieldName:
            "Financial.Reports[0].ProfitLossFromOrdinaryOperatingActivities.Value",
          filterName: "economy-operating-profit-value",
        },
        change: {
          fieldName:
            "Financial.Reports[0].ProfitLossFromOrdinaryOperatingActivities.Change",
          filterName: "economy-operating-profit-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-operating-profit-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-operating-profit-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    equity: createFilterGroup({
      id: "equity",
      title: "Egenkapital",
      keywords: [],
      description:
        "Virksomhedernes egenkapital, der er alle deres aktiver fratrukket gæld.",
      filterType: filterGroupTypes.economy,
      type: "number",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i egenkapital over tid, når man sammenligner de 2 seneste regnskaber.",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].Equity.Value",
          filterName: "economy-equity-value",
        },
        change: {
          fieldName: "Financial.Reports[0].Equity.Change",
          filterName: "economy-equity-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-equity-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-equity-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    assets: createFilterGroup({
      id: "assets",
      title: "Aktiver",
      keywords: [],
      description:
        "Virksomhedernes aktiver, der er summen af egenkapital plus gæld.",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].Assets.Value",
          filterName: "economy-assets-value",
        },
        change: {
          fieldName: "Financial.Reports[0].Assets.Change",
          filterName: "economy-assets-change",
        },
      },
      filterType: filterGroupTypes.economy,
      type: "number",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i aktiver over tid, når man sammenligner de 2 seneste regnskaber.",
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-assets-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-assets-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    liabilitiesandequity: createFilterGroup({
      id: "liabilitiesandequity",
      title: "Passiver",
      keywords: ["gæld"],
      description:
        "Viser virksomhedernes samlede passiver, der er gæld + egenkapital.",
      filterType: filterGroupTypes.economy,
      type: "number",
      hasGrowth: true,
      minRange: -100,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i passiver over tid, når man sammenligner de 2 seneste regnskaber.",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].LiabilitiesAndEquity.Value",
          filterName: "economy-liabilitiesandequity-value",
        },
        change: {
          fieldName: "Financial.Reports[0].LiabilitiesAndEquity.Change",
          filterName: "economy-liabilitiesandequity-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-liabilitiesandequity-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-liabilitiesandequity-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    currentAssets: createFilterGroup({
      id: "current-assets",
      title: "Omsætningsaktiver",
      keywords: [],
      description:
        "Viser virksomhedernes omsætningsaktiver, der kan omsættes inden for ét år.",
      filterType: filterGroupTypes.economy,
      type: "number",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].CurrentAssets.Value",
          filterName: "economy-current-assets-value",
        },
        change: {
          fieldName: "Financial.Reports[0].CurrentAssets.Change",
          filterName: "economy-current-assets-change",
        },
      },
      hasGrowth: true,
      minRange: -100,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i omsætningsaktiver over tid, når man sammenligner de 2 seneste regnskaber.",
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-current-assets-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-current-assets-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    inventories: createFilterGroup({
      id: "inventories",
      title: "Varebeholdning",
      keywords: ["varelager", "lager", "beholdning"],
      description:
        "Viser værdien af virksomhedernes varebeholdninger, f.eks. råvarer, varer under fremstilling og færdigvarer.",
      filterType: filterGroupTypes.economy,
      type: "number",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].Inventories.Value",
          filterName: "economy-inventories-value",
        },
        change: {
          fieldName: "Financial.Reports[0].Inventories.Change",
          filterName: "economy-inventories-change",
        },
      },
      hasGrowth: true,
      minRange: -100,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i varebeholdning over tid, når man sammenligner de 2 seneste regnskaber.",
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-inventories-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-inventories-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    staffCosts: createFilterGroup({
      id: "staff-costs",
      title: "Personaleomkostninger",
      keywords: [
        "lønninger",
        "medarbejderomkostninger",
        "personalegoder",
        "feriepenge",
        "pension",
        "sociale omkostninger",
        "ansatte",
        "lønomkostninger",
        "arbejdsgiveromkostninger",
      ],
      description: "Viser virksomhedernes samlede omkostninger til personale.",
      filterType: filterGroupTypes.economy,
      hasGrowth: true,
      type: "number",
      filters: {
        value: {
          fieldName: "Financial.Reports[0].EmployeeBenefitsExpense.Value",
          filterName: "economy-staff-costs-value",
        },
        change: {
          fieldName: "Financial.Reports[0].EmployeeBenefitsExpense.Change",
          filterName: "economy-staff-costs-change",
        },
      },
      minRange: -100,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i personaleomkostninger over tid, når man sammenligner de 2 seneste regnskaber.",
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-staff-costs-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-staff-costs-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    pensionContributions: createFilterGroup({
      id: "pension-contributions",
      title: "Pensionsindbetaling pr. medarbejder",
      keywords: ["personale", "ansatte"],
      description:
        "Viser virksomhedernes årlige gennemsnitlige pensionsindbetalinger pr. medarbejder.",
      filterType: filterGroupTypes.economy,
      type: "number",
      filters: {
        value: {
          fieldName:
            "Financial.Reports[0].PostEmploymentBenefitExpensePerEmployee.Value",
          filterName: "economy-pension-contributions-value",
        },
        change: {
          fieldName:
            "Financial.Reports[0].PostEmploymentBenefitExpensePerEmployee.Change",
          filterName: "economy-pension-contributions-change",
        },
      },
      hasGrowth: true,
      minRange: -100,
      maxRange: 500,
      growthTitle: "Udvikling",
      growthDescription:
        "Vækst eller fald i pensionsindbetalinger over tid, når man sammenligner de 2 seneste regnskaber.",
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-pension-contributions-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-pension-contributions-change",
          filters,
        );
        return createDisplayValue(valueFilter, changeFilter, ignoreOperator);
      },
    }),
    solvencyRatio: createFilterGroup({
      id: "solvency-ratio",
      title: "Soliditetsgrad",
      keywords: [],
      description:
        "Viser hvor stor en del af en virksomheds aktiver der er finansieret med egenkapital",
      filterType: filterGroupTypes.economy,
      type: "percentage",
      growthDescription: "Vækst eller fald i soliditetsgrad over tid.",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      filters: {
        value: {
          fieldName: "Financial.Reports[0].SolvencyRatio.Value",
          filterName: "economy-solvency-ratio-value",
        },
        change: {
          fieldName: "Financial.Reports[0].SolvencyRatio.Change",
          filterName: "economy-solvency-ratio-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const valueFilter = getFilterByFilterName(
          "economy-solvency-ratio-value",
          filters,
        );
        const changeFilter = getFilterByFilterName(
          "economy-solvency-ratio-change",
          filters,
        );
        return createDisplayValue(
          valueFilter,
          changeFilter,
          ignoreOperator,
          "percentage",
        );
      },
    }),
    realEquityRatio: createFilterGroup({
      id: "real-equity-ratio",
      title: "Reel soliditetsgrad",
      keywords: [],
      description:
        // "Viser virksomhedens reelle økonomiske styrke ved at tage højde for fradrag af immaterielle værdier og tillæg af ansvarlig kapital. Angiver, hvor stor en del af aktiverne der reelt er finansieret med egenkapital.",
        "Den reelle soliditetsgrad er en justeret udgave af den traditionelle soliditetsgrad, som giver et mere retvisende billede af virksomhedens økonomiske styrke. I beregningen fratrækkes poster som udbytte, immaterielle anlægsaktiver, hensættelser til udskudt skat og indretning af lejede lokaler for at fjerne ikke-omsættelige værdier og dermed vise virksomhedens reelle soliditet.",
      filterType: filterGroupTypes.economy,
      type: "percentage",
      growthDescription: "Vækst eller fald i reel soliditetsgrad over tid.",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      filters: {
        value: {
          fieldName: "Financial.Reports[0].AdjustedEquityRatio.Value",
          filterName: "economy-real-equity-ratio-value",
        },
        change: {
          fieldName: "Financial.Reports[0].AdjustedEquityRatio.Change",
          filterName: "economy-real-equity-ratio-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const realEquityRatioFilter = getFilterByFilterName(
          "economy-real-equity-ratio-value",
          filters,
        );

        if (ignoreOperator) {
          return realEquityRatioFilter?.values?.[0]
            ? formatPercentage(
                parseInt(realEquityRatioFilter?.values?.[0]) / 100,
              )
            : "-";
        }

        return createRangeDisplayValue(realEquityRatioFilter, "percentage");
      },
    }),
    returnOnAssets: createFilterGroup({
      id: "return-on-assets",
      title: "Afkastningsgrad",
      keywords: ["rentabilitet", "roi"],
      description:
        "Viser virksomhedernes rentabilitet og effektive udnyttelse af aktiver.",
      filterType: filterGroupTypes.economy,
      type: "percentage",
      growthDescription: "Vækst eller fald i afkastningsgrad over tid.",
      hasGrowth: true,
      minRange: -500,
      maxRange: 500,
      filters: {
        value: {
          fieldName: "Financial.Reports[0].ROI.Value",
          filterName: "economy-return-on-assets-value",
        },
        change: {
          fieldName: "Financial.Reports[0].ROI.Change",
          filterName: "economy-return-on-assets-change",
        },
      },
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const returnOnAssetsFilter = getFilterByFilterName(
          "economy-return-on-assets-value",
          filters,
        );

        if (ignoreOperator) {
          return returnOnAssetsFilter?.values?.[0]
            ? formatPercentage(
                parseInt(returnOnAssetsFilter?.values?.[0]) / 100,
              )
            : "-";
        }

        switch (returnOnAssetsFilter?.operator) {
          case "GreaterThan":
            return `Over ${returnOnAssetsFilter.values?.[0]}%`;
          case "LessThan":
            return `Under ${returnOnAssetsFilter.values?.[0]}%`;
          case "Between":
            return `${returnOnAssetsFilter.values?.[0]}%`.concat(
              " - ",
              `${returnOnAssetsFilter.values?.[1]}%`,
            );
          default:
            return "Ikke angivet";
        }
      },
    }),
    valuation: createFilterGroup({
      id: "valuation",
      title: "Værdiansættelse",
      keywords: ["kurs", "valuation"],
      description:
        "Viser virksomhedernes værdiansættelser ud fra selskabskapital og kurs ved seneste kapitalændringer.",
      filterType: filterGroupTypes.economy,
      hasGrowth: false,
      type: "number",
      filters: {
        value: {
          fieldName: "Valuation.Valuation",
          filterName: "economy-valuation-value",
        },
        change: null,
      },
      DisplayValue: ({ filters, ignoreOperator }) => {
        const valueFilter = getFilterByFilterName(
          "economy-valuation-value",
          filters,
        );

        if (ignoreOperator) {
          return valueFilter?.values?.[0]
            ? formatMoney(parseInt(valueFilter?.values?.[0]))
            : "-";
        }

        return createRangeDisplayValue(valueFilter);
      },
    }),
    typeOfAuditorAssistance: createFilterGroup({
      id: "type-of-auditor-assistance",
      title: "Type af revisorbistand",
      keywords: ["revision", "erklæring"],
      description:
        "Angiver niveauet for, hvor grundigt regnskabet er gennemgået af en revisor. En lavere erklæring betyder begrænset eller ingen gennemgang, mens en højere erklæring (som udvidet gennemgang eller fuld revision) giver større sikkerhed for regnskabets pålidelighed.",
      filterType: filterGroupTypes.typeOfAuditorAssistance, // TODO - added filterGroupType, should create general Select filterGroupType?
      filters: {
        value: {
          fieldName: "Financial.Reports[0].TypeOfAuditorAssistanceConverted",
          filterName: "economy-type-of-auditor-assistance-value",
        },
      },
      DisplayValue: ({ filters }) => {
        const typeOfAuditorAssistanceFilter = getFilterByFilterName(
          "economy-type-of-auditor-assistance-value",
          filters,
        );
        if (!typeOfAuditorAssistanceFilter?.values?.length) {
          return "Ikke angivet";
        }

        return typeOfAuditorAssistanceFilter.values
          .map((value) => {
            return typeOfAuditorAssistanceOptions.find(
              (m) => m.value === parseInt(value),
            )?.title;
          })
          .join(", ");
      },
    }),
  },
} satisfies FilterCategory;
